import React, { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  CircularProgress,
  Pagination,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Autocomplete,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  Clear as ClearIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { API_BASE_URL, collectionFileDisplayUrl } from '../api';
import apiService from '../services/apiService';
import {
  fetchAllCollectionsFromApi,
  filterJobsByOwnerList,
  getCachedJobsList,
  setCachedJobsList,
} from '../utils/jobsListCache';
import { LEAD_SOURCE_OPTIONS, labelForLeadSource } from '../constants/leadSources';
import { resolveRoleName } from '../constants/roles';
import {
  countNewOnlineBookings,
  isNewOnlineBooking,
  JOBS_FILTER_NEW_ONLINE,
  NEW_ONLINE_BOOKING_LABEL,
} from '../utils/onlineBooking';
import { getUiPreferences, patchSavedFiltersKey } from '../userPreferencesApi';
import { useLocation, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import dayjs from 'dayjs';
import { parseJobScheduledStart } from '../utils/jobSchedule';
import JobCostingPanel from '../components/JobCostingPanel';
import JobInventoryPanel from '../components/JobInventoryPanel';
import JobEnteredScheduledDates from '../components/JobEnteredScheduledDates';
import SendMessageDialog from '../components/SendMessageDialog';

/** When listing jobs for one calendar day: match scheduled start, else creation date. */
function jobMatchesScheduleDayFilter(job, filterYmd) {
  if (!filterYmd || String(filterYmd).trim() === '') return true;
  const start = parseJobScheduledStart(job);
  if (start && start.isValid()) {
    return start.format('YYYY-MM-DD') === filterYmd;
  }
  if (job.created_at) {
    return dayjs(job.created_at).format('YYYY-MM-DD') === filterYmd;
  }
  return false;
}

/** Upload field files (photos/docs) to an existing collection. */
async function uploadCollectionFiles(collectionId, files, token) {
  if (!collectionId || !files?.length) {
    return { ok: true };
  }
  const fd = new FormData();
  files.forEach((file) => {
    fd.append('files[]', file);
  });
  const response = await fetch(`${API_BASE_URL}collections/${collectionId}/upload`, {
    method: 'POST',
    body: fd,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      Accept: 'application/json',
    },
  });
  if (!response.ok) {
    const json = await response.json().catch(() => ({}));
    return {
      ok: false,
      message: json.error || json.message || `Upload failed (${response.status})`,
    };
  }
  return { ok: true };
}

/** API may return is_booked as bool, 0/1, or strings — normalize for UI and filters. */
function isBookedBool(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value === null || value === undefined) return false;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === '1' || s === 'true' || s === 'yes') return true;
    if (s === '0' || s === 'false' || s === '') return false;
  }
  return Boolean(value);
}

/** Strip zero-width / BOM — prefs, paste, mobile keyboards can break string equality and trim(). */
function normalizeJobSearchInput(s) {
  return String(s || '')
    .replace(/[\u200b-\u200d\ufeff]/g, '')
    .trim();
}

function readStoredUserRoleName() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    return resolveRoleName(u?.role);
  } catch {
    return null;
  }
}

/**
 * Only restore safe prefs on mount (pagination + status).
 * Do NOT restore clientSearch / scheduleDayFilter / ownerFilter — they often hide every job after navigation.
 */
function readInitialJobsListPrefs() {
  const j = getUiPreferences().savedFilters?.jobs;
  const out = {
    filter: 'all',
    bookedFilter: 'all',
    ownerFilter: 'all',
    clientSearch: '',
    scheduleDayFilter: '',
    currentPage: 1,
    itemsPerPage: 6,
  };
  if (!j || typeof j !== 'object') return out;
  if (['all', 'active', 'completed', 'cancelled'].includes(j.filter)) out.filter = j.filter;
  if (['all', 'booked', 'unbooked'].includes(j.bookedFilter)) out.bookedFilter = j.bookedFilter;
  const ipp = Number(j.itemsPerPage);
  if ([5, 6, 10, 20, 50].includes(ipp)) out.itemsPerPage = ipp;
  return out;
}

function dedupeJobsById(rows) {
  const seen = new Set();
  const unique = [];
  for (const row of rows) {
    const id = row?.id;
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    unique.push(row);
  }
  return unique;
}

export default function Jobs() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const userRole = (() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const u = JSON.parse(raw);
      return u?.role || null;
    } catch {
      return null;
    }
  })();
  const userRoleName = resolveRoleName(userRole);
  const mayDeleteJobs = userRoleName && ['super_admin', 'admin', 'manager'].includes(userRoleName);
  const canUseAi = userRoleName && ['super_admin', 'admin', 'manager', 'office_admin'].includes(userRoleName);
  const canMarkJobFieldStatus =
    userRoleName && ['super_admin', 'admin', 'manager', 'office_admin'].includes(userRoleName);
  const [initialListPrefs] = useState(() => readInitialJobsListPrefs());
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentUserId = useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const u = JSON.parse(raw);
      return u?.id ?? null;
    } catch {
      return null;
    }
  }, []);
  const [openDialog, setOpenDialog] = useState(false);
  const [newJob, setNewJob] = useState({ 
    name: '', 
    client: '', 
    client_id: '',
    lead_source: 'phone',
    is_booked: false,
    address: '', 
    email: '',
    phone: '',
    scheduled_date: '', 
    notes: '',
    price: '',
    currency: 'GBP',
    payment_method: 'cash',
    is_problem: false,
    problem_reason: '',
    files: [],
    lat: null,
    lng: null,
    driver_id: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editSelectedFiles, setEditSelectedFiles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [createError, setCreateError] = useState('');
  const [dailyPdfDate, setDailyPdfDate] = useState(() => new Date().toISOString().slice(0, 10));
  /** YYYY-MM-DD or '' — filter list/map counts to jobs scheduled (or created) on that day */
  const [scheduleDayFilter, setScheduleDayFilter] = useState(initialListPrefs.scheduleDayFilter);
  
  // Job details and edit state
  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);
  const [jobStatusUpdating, setJobStatusUpdating] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [jobToCancel, setJobToCancel] = useState(null);
  const [sendMessageOpen, setSendMessageOpen] = useState(false);
  const [editJobOpen, setEditJobOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobSummary, setJobSummary] = useState('');
  const [jobSummaryMode, setJobSummaryMode] = useState('');
  const [jobSummaryLoading, setJobSummaryLoading] = useState(false);
  const [editJob, setEditJob] = useState({ 
    name: '', 
    client: '', 
    client_id: '',
    lead_source: 'phone',
    is_booked: false,
    address: '', 
    email: '',
    phone: '',
    scheduled_date: '', 
    notes: '',
    price: '',
    currency: 'GBP',
    payment_method: 'cash',
    is_problem: false,
    problem_reason: '',
    driver_id: '',
  });

  const splitDateTimeLocal = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return { date: '', time: '' };
    // datetime-local comes as "YYYY-MM-DDTHH:mm"
    if (raw.includes('T')) {
      const [d, t] = raw.split('T');
      return { date: d || '', time: (t || '').slice(0, 5) };
    }
    // date only
    return { date: raw.slice(0, 10), time: '' };
  };

  const combineDateAndTime = (date, time) => {
    const d = String(date || '').trim().slice(0, 10);
    const t = String(time || '').trim().slice(0, 5);
    if (!d) return '';
    if (!t) return `${d}T00:00`;
    return `${d}T${t}`;
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialListPrefs.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialListPrefs.itemsPerPage);
  const [filter, setFilter] = useState(initialListPrefs.filter); // all, active, completed
  const [bookedFilter, setBookedFilter] = useState(initialListPrefs.bookedFilter); // all | booked | unbooked
  const [newOnlineOnlyFilter, setNewOnlineOnlyFilter] = useState(false);
  // ownerFilter:
  // - 'all' => no extra owner filtering
  // - 'mine' => mine=1 (office_owner_user_id == current user)
  // - 'office_<id>' => office_owner_user_id == <id> (super_admin only)
  const [ownerFilter, setOwnerFilter] = useState(initialListPrefs.ownerFilter);
  const [officeAdmins, setOfficeAdmins] = useState([]);
  const [officeAdminsLoading, setOfficeAdminsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState(initialListPrefs.clientSearch);
  const [clientSearchOptions, setClientSearchOptions] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const clientSearchDebounceRef = useRef(null);
  const clientSearchSeqRef = useRef(0);
  const jobsFetchIdRef = useRef(0);
  const jobsRetryTimerRef = useRef(null);
  const jobsRetryCountRef = useRef(0);
  const jobsRef = useRef([]);
  const didAutoClearFiltersRef = useRef(false);
  const [jobsLoadError, setJobsLoadError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [jobLocations, setJobLocations] = useState([]); // [{id, name, address, lat, lng, status}]
  const mapRef = useRef();
  const [translatedAddresses, setTranslatedAddresses] = useState({}); // {jobId: translatedAddress}

  // Helper component to recenter map when coordinates change
  const MapUpdater = ({ lat, lng }) => {
    const map = useMap();
    useEffect(() => {
      if (lat && lng) {
        map.setView([lat, lng], 15, { animate: true });
      }
    }, [lat, lng, map]);
    return null;
  };
  
  // Function to fit map bounds to show all jobs
  const fitMapToJobs = () => {
    if (mapRef.current && jobLocations.length > 0) {
      const map = mapRef.current;
      const bounds = L.latLngBounds(jobLocations.map(job => [job.lat, job.lng]));
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  };

  useEffect(() => {
    if (location.state?.[JOBS_FILTER_NEW_ONLINE]) {
      setNewOnlineOnlyFilter(true);
      setFilter('all');
      setCurrentPage(1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Handle auto-opening modals from Dashboard / Leads deep link
  useEffect(() => {
    const stateJobId = location.state?.jobId;
    if (!stateJobId) return;

    const targetId = Number(stateJobId);
    if (!Number.isFinite(targetId)) return;

    const openJobModal = (targetJob) => {
      if (!targetJob) return;
        if (location.state.action === 'edit') {
          setSelectedJob(targetJob);
          const jobDriverRaw = String(targetJob.driver || '').trim();
          const matchedDriver = jobDriverRaw
            ? drivers.find((d) => {
                const n = String(d?.name || '').trim();
                const e = String(d?.email || '').trim();
                return (n && n === jobDriverRaw) || (e && e === jobDriverRaw);
              })
            : null;
          setEditJob({
            name: targetJob.name || '',
            client: targetJob.client || '',
            client_id: targetJob.client_id ?? targetJob.client_record?.id ?? '',
            lead_source: targetJob.lead_source || 'phone',
            is_booked: isBookedBool(targetJob.is_booked),
            address: targetJob.address || '',
            email: targetJob.email || '',
            phone: targetJob.phone || '',
            scheduled_date: targetJob.scheduled_date
              ? combineDateAndTime(targetJob.scheduled_date, targetJob.scheduled_time)
              : '',
            notes: targetJob.notes || '',
            price: targetJob.price ?? '',
            currency: targetJob.currency || 'GBP',
            payment_method: targetJob.payment_method || 'cash',
            is_problem: !!targetJob.is_problem,
            problem_reason: targetJob.problem_reason || '',
            driver_id: matchedDriver?.id ?? '',
          });
          setEditJobOpen(true);
        } else if (location.state.action === 'view') {
          setSelectedJob(targetJob);
          setJobDetailsOpen(true);
        }
        
      navigate(location.pathname, { replace: true, state: {} });
    };

    const targetJob = jobs.find((j) => Number(j.id) === targetId);
    if (targetJob) {
      openJobModal(targetJob);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}collections/${targetId}`, {
          headers: token
            ? { Authorization: `Bearer ${token}`, Accept: 'application/json' }
            : { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const fetched = await response.json();
        if (!cancelled && fetched?.id) {
          openJobModal(fetched);
        }
      } catch {
        /* job not found or forbidden — ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobs, location.state, location.pathname, navigate, drivers]);

  useEffect(() => {
    if (location.state?.action === 'create') {
      // Automatically open the Create New Job modal
      setNewJob({ 
        name: '', 
        client: '', 
        client_id: '',
        address: '', 
        email: '',
        phone: '',
        scheduled_date: '', 
        notes: '',
        files: [],
        lat: null,
        lng: null,
        driver_id: ''
      });
      setSelectedFiles([]);
      setOpenDialog(true);
      
      // Clear state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [jobs, location, navigate, drivers]);

  useEffect(() => {
    // Geocoding active job addresses (respect schedule-day filter like the list when not searching)
    const fetchLocations = async () => {
      const qSearch = normalizeJobSearchInput(clientSearch);
      const jobsScoped =
        scheduleDayFilter && !qSearch
          ? jobs.filter((j) => jobMatchesScheduleDayFilter(j, scheduleDayFilter))
          : jobs;

      console.log('Jobs for geocoding:', jobsScoped);
      console.log('Job statuses found:', jobsScoped.map(j => ({ id: j.id, name: j.name, status: j.status, address: j.address })));
      
      // Check for different status variations
      const statuses = [...new Set(jobsScoped.map(j => j.status))];
      console.log('All unique statuses found:', statuses);
      
      // Filter jobs that are NOT completed (case-insensitive) and have addresses
      const activeJobs = jobsScoped.filter(j => {
        const status = j.status ? j.status.toLowerCase() : '';
        const isCompleted = status === 'completed' || status === 'completed' || status === 'finished' || status === 'done';
        return !isCompleted && j.address;
      });
      console.log('Active jobs with addresses:', activeJobs);
      console.log('Jobs without addresses:', jobsScoped.filter(j => !j.address));
      console.log('Jobs with addresses:', jobsScoped.filter(j => j.address));
      
      const locations = await Promise.all(activeJobs.map(async (job) => {
        // If already has lat/lng in job, use them
        if (job.lat && job.lng && !isNaN(job.lat) && !isNaN(job.lng)) {
          console.log('Job already has valid coordinates:', job.name, job.lat, job.lng);
          return { ...job, lat: parseFloat(job.lat), lng: parseFloat(job.lng) };
        }
        // Otherwise geocode the address
        try {
          console.log('Geocoding address for job:', job.name, job.address);
          // Add "London, UK" to improve geocoding accuracy for London addresses
          const searchQuery = job.address.includes('London') ? job.address : `${job.address}, London, UK`;
          console.log('Search query for geocoding:', searchQuery);
          
          const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&countrycodes=gb&limit=1`);
          const data = await res.json();
          console.log('Geocoding response for job:', job.name, data);
          
          if (data && data[0]) {
            console.log('Geocoding successful for job:', job.name, data[0].lat, data[0].lon);
            return { ...job, lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
          } else {
            console.log('No geocoding results for job:', job.name);
            // Fallback: use London coordinates if geocoding fails
            console.log('Using fallback London coordinates for job:', job.name);
            // Add small offset to avoid overlapping markers
            const offset = Math.random() * 0.01;
            return { ...job, lat: 51.5074 + offset, lng: -0.1278 + offset };
          }
        } catch (e) {
          console.error('Geocoding error for job:', job.name, e);
          // Fallback: use London coordinates if geocoding fails
          console.log('Using fallback London coordinates for job:', job.name);
          return { ...job, lat: 51.5074, lng: -0.1278 };
        }
      }));
      
      const validLocations = locations.filter(Boolean);
      console.log('Final job locations:', validLocations);
      setJobLocations(validLocations);
    };
    if (jobs.length === 0) {
      setJobLocations([]);
      return undefined;
    }
    const t = setTimeout(() => {
      void fetchLocations();
    }, 300);
    return () => clearTimeout(t);
  }, [jobs, scheduleDayFilter, clientSearch]);

  useEffect(() => {
            // Translate addresses from Cyrillic
    const translateAddresses = async () => {
      const updates = {};
      await Promise.all(jobLocations.map(async (job) => {
        if (job.address && /[\u0400-\u04FF]/.test(job.address)) {
          try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(job.address)}&accept-language=en`);
            const data = await res.json();
            if (data && data[0]) {
              updates[job.id] = data[0].display_name;
            }
          } catch (e) {}
        }
      }));
      if (Object.keys(updates).length > 0) {
        setTranslatedAddresses(prev => ({ ...prev, ...updates }));
      }
    };
    if (jobLocations.length > 0) translateAddresses();
  }, [jobLocations]);

  // Auto-fit map to show all jobs when jobLocations change
  useEffect(() => {
    if (jobLocations.length > 0) {
      // Small delay to ensure map is rendered
      setTimeout(() => {
        fitMapToJobs();
      }, 500);
    }
  }, [jobLocations]);

  const applyJobsRows = useCallback((rows, { replace = true } = {}) => {
    const unique = dedupeJobsById(rows);
    if (!replace && unique.length === 0 && jobsRef.current.length > 0) {
      return jobsRef.current;
    }
    jobsRef.current = unique;
    setJobs(unique);
    setJobsLoadError('');
    jobsRetryCountRef.current = 0;
    if (unique.length > 0) {
      setCurrentPage(1);
    }
    return unique;
  }, []);

  const fetchJobs = useCallback(async ({ force = false } = {}) => {
    const fetchId = ++jobsFetchIdRef.current;

    if (force) {
      jobsRetryCountRef.current = 0;
      if (jobsRetryTimerRef.current) {
        clearTimeout(jobsRetryTimerRef.current);
        jobsRetryTimerRef.current = null;
      }
    }

    setLoading(true);
    setRefreshing(jobsRef.current.length > 0);
    setJobsLoadError('');

    try {
      if (!apiService.getToken()) {
        throw new Error('Not signed in. Please log in again.');
      }

      if (!force) {
        const cached = getCachedJobsList();
        if (cached?.length && fetchId === jobsFetchIdRef.current) {
          applyJobsRows(cached, { replace: true });
          setLoading(false);
          setRefreshing(false);
        }
      }

      const allRows = await fetchAllCollectionsFromApi();
      if (fetchId !== jobsFetchIdRef.current) {
        return;
      }

      const unique = applyJobsRows(allRows, { replace: true });
      setCachedJobsList(unique);

      if (unique.length === 0) {
        setJobsLoadError('Server returned 0 jobs. Check filters or tap Reload.');
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      if (fetchId === jobsFetchIdRef.current) {
        const msg =
          error instanceof Error ? error.message : 'Could not load jobs';
        setJobsLoadError(msg);
        if (jobsRef.current.length === 0 && jobsRetryCountRef.current < 2 && !force) {
          jobsRetryCountRef.current += 1;
          jobsRetryTimerRef.current = setTimeout(() => {
            jobsRetryTimerRef.current = null;
            void fetchJobs({ force: false });
          }, 1200);
        }
      }
    } finally {
      if (fetchId === jobsFetchIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [applyJobsRows]);

  const persistJobsListFilters = useCallback((partial) => {
    const snapshot = {
      filter: 'all',
      bookedFilter: 'all',
      ownerFilter: 'all',
      clientSearch: '',
      scheduleDayFilter: '',
      currentPage: 1,
      itemsPerPage,
      ...partial,
    };
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        const prefs =
          u.ui_preferences && typeof u.ui_preferences === 'object' ? u.ui_preferences : {};
        const sf =
          prefs.savedFilters && typeof prefs.savedFilters === 'object'
            ? prefs.savedFilters
            : {};
        u.ui_preferences = {
          ...prefs,
          savedFilters: {
            ...sf,
            jobs: {
              ...(sf.jobs && typeof sf.jobs === 'object' ? sf.jobs : {}),
              ...snapshot,
            },
          },
        };
        localStorage.setItem('user', JSON.stringify(u));
      }
    } catch {
      /* ignore */
    }
    patchSavedFiltersKey('jobs', snapshot).catch(() => {});
  }, [itemsPerPage]);

  const clearListFilters = useCallback(() => {
    setFilter('all');
    setBookedFilter('all');
    setClientSearch('');
    setScheduleDayFilter('');
    setNewOnlineOnlyFilter(false);
    setOwnerFilter('all');
    setCurrentPage(1);
    persistJobsListFilters({
      filter: 'all',
      bookedFilter: 'all',
      ownerFilter: 'all',
      clientSearch: '',
      scheduleDayFilter: '',
      currentPage: 1,
    });
  }, [persistJobsListFilters]);

  const reloadJobsList = useCallback(() => {
    clearListFilters();
    if (jobsRef.current.length === 0) {
      void fetchJobs({ force: true });
    }
  }, [clearListFilters, fetchJobs]);

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}drivers`, {
        headers: token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' }
      });
      const data = await response.json();
      setDrivers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const mergeClientRecordIntoList = (list, rec) => {
    const rows = Array.isArray(list) ? list : [];
    if (!rec?.id) return rows;
    if (rows.some((c) => Number(c.id) === Number(rec.id))) return rows;
    return [rec, ...rows];
  };

  const unwrapClientsResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
  };

  const runClientFetch = async (rawQuery, mergeRecord) => {
    const q = (rawQuery || '').trim();
    const seq = ++clientSearchSeqRef.current;
    setClientSearchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams({ search: '1', limit: '50' });
      if (q) qs.set('q', q);
      const response = await fetch(`${API_BASE_URL}clients?${qs}`, {
        headers: token
          ? { Authorization: `Bearer ${token}`, Accept: 'application/json' }
          : { Accept: 'application/json' },
      });
      const raw = response.ok ? await response.json() : [];
      const list = unwrapClientsResponse(raw);
      if (seq !== clientSearchSeqRef.current) return;
      setClientSearchOptions(mergeClientRecordIntoList(list, mergeRecord));
    } catch (e) {
      console.error('Error searching clients:', e);
      if (seq !== clientSearchSeqRef.current) return;
      setClientSearchOptions(mergeClientRecordIntoList([], mergeRecord));
    } finally {
      if (seq === clientSearchSeqRef.current) {
        setClientSearchLoading(false);
      }
    }
  };

  /** Empty query runs immediately; non-empty search is debounced. */
  const fetchClientsForJobForm = (rawQuery, mergeRecord) => {
    const q = (rawQuery || '').trim();
    clearTimeout(clientSearchDebounceRef.current);
    if (q === '') {
      void runClientFetch('', mergeRecord);
      return;
    }
    clientSearchDebounceRef.current = setTimeout(() => {
      void runClientFetch(rawQuery, mergeRecord);
    }, 200);
  };

  useLayoutEffect(() => {
    const cached = getCachedJobsList();
    if (cached?.length) {
      applyJobsRows(cached, { replace: true });
      setLoading(false);
    }
    void fetchJobs({ force: false });

    const onPageShow = (event) => {
      if (event.persisted) {
        void fetchJobs({ force: true });
      }
    };
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pageshow', onPageShow);
      if (jobsRetryTimerRef.current) {
        clearTimeout(jobsRetryTimerRef.current);
        jobsRetryTimerRef.current = null;
      }
    };
  }, [location.pathname, location.key, applyJobsRows, fetchJobs]);

  useEffect(() => {
    fetchDrivers();
  }, []);

  // Only super_admin sees office_admin users in Owner dropdown.
  useEffect(() => {
    if (userRole !== 'super_admin') return undefined;

    let cancelled = false;
    const loadOfficeAdmins = async () => {
      try {
        setOfficeAdminsLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}users`, {
          headers: token
            ? { Authorization: `Bearer ${token}`, Accept: 'application/json' }
            : { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) return;
        const data = await response.json();
        const admins = Array.isArray(data)
          ? data.filter((u) => u?.role?.name === 'office_admin')
          : [];
        if (cancelled) return;
        setOfficeAdmins(admins);
      } catch (e) {
        // keep existing list on errors
      } finally {
        if (!cancelled) setOfficeAdminsLoading(false);
      }
    };

    void loadOfficeAdmins();
    const t = setInterval(() => void loadOfficeAdmins(), 120000);

    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [userRole]);

  // If selected office admin was deleted, reset to "All owners".
  useEffect(() => {
    if (userRole !== 'super_admin') return undefined;
    if (typeof ownerFilter !== 'string' || !ownerFilter.startsWith('office_')) return undefined;

    const idStr = ownerFilter.slice('office_'.length);
    const idNum = Number(idStr);
    if (!Number.isFinite(idNum)) {
      setOwnerFilter('all');
      return undefined;
    }

    const exists = officeAdmins.some((u) => Number(u?.id) === idNum);
    if (!exists) setOwnerFilter('all');
    return undefined;
  }, [officeAdmins, ownerFilter, userRole]);

  useEffect(() => {
    if (loading) return undefined;
    const t = setTimeout(() => {
      patchSavedFiltersKey('jobs', {
        filter,
        bookedFilter,
        clientSearch,
        scheduleDayFilter,
        ownerFilter,
        currentPage,
        itemsPerPage,
      }).catch(() => {});
    }, 900);
    return () => clearTimeout(t);
  }, [filter, bookedFilter, clientSearch, scheduleDayFilter, ownerFilter, currentPage, itemsPerPage, loading]);

  useEffect(() => {
    if (openDialog || editJobOpen) {
      const merge = editJobOpen ? selectedJob?.client_record : null;
      fetchClientsForJobForm('', merge);
    }
  }, [openDialog, editJobOpen, selectedJob]);

  const handleCreateJob = async () => {
    try {
      setCreateError('');
      setCreateLoading(true);
      const newJobClientId = resolveClientIdForJobSubmit(
        newJob.client_id,
        newJob.client,
        clientSearchOptions,
        null
      );
      const payload = {
        name: newJob.name,
        client: newJob.client,
        address: newJob.address,
        notes: newJob.notes,
        lead_source: newJob.lead_source || 'phone',
        is_booked: isBookedBool(newJob.is_booked),
        ...(newJob.price !== '' && newJob.price != null ? { price: Number(newJob.price) } : {}),
        ...(newJob.price !== '' && newJob.price != null ? { currency: newJob.currency || 'GBP' } : {}),
        payment_method: newJob.payment_method || 'cash',
        is_problem: !!newJob.is_problem,
        ...(newJob.is_problem ? { problem_reason: newJob.problem_reason || '' } : { problem_reason: '' }),
        ...(newJob.email ? { email: newJob.email } : {}),
        ...(newJob.phone ? { phone: newJob.phone } : {}),
      };
      const dt = splitDateTimeLocal(newJob.scheduled_date);
      if (dt.date) payload.scheduled_date = dt.date;
      if (dt.time) payload.scheduled_time = dt.time;
      if (newJobClientId != null && newJobClientId !== '') {
        payload.client_id = String(newJobClientId);
      }
      if (newJob.driver_id) {
        // Find driver name by ID
        const selectedDriver = drivers.find(d => d.id.toString() === newJob.driver_id.toString());
        if (selectedDriver) {
          payload.driver = selectedDriver.name;
        }
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}collections`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (response.ok) {
        const created = await response.json().catch(() => null);
        const createdId = created?.id;

        // Upload attachments (if any) via the dedicated endpoint.
        if (createdId && selectedFiles.length > 0) {
          const uploadRes = await uploadCollectionFiles(createdId, selectedFiles, token);
          if (!uploadRes.ok) {
            window.alert(
              `Job was created, but some files could not be uploaded: ${uploadRes.message}`
            );
          }
        }

        setOpenDialog(false);
        setNewJob({ 
          name: '', 
          client: '', 
          client_id: '',
        lead_source: 'phone',
        is_booked: false,
          address: '', 
          email: '',
          phone: '',
          scheduled_date: '', 
          notes: '',
        price: '',
        currency: 'GBP',
        payment_method: 'cash',
        is_problem: false,
        problem_reason: '',
          files: [],
          lat: null,
          lng: null,
          driver_id: ''
        });
        setSelectedFiles([]);
        setClientSearchOptions([]);
        void fetchJobs({ force: true });
      } else {
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const err = await response.json();
          setCreateError(err.message || 'Failed to create job');
        } else {
          const text = await response.text();
          setCreateError(text || 'Failed to create job');
        }
      }
    } catch (error) {
      console.error('Error creating job:', error);
      setCreateError('Network error while creating job');
    } finally {
      setCreateLoading(false);
    }
  };

  // View job details
  const handleViewJob = (job) => {
    setSelectedJob(job);
    setJobSummary('');
    setJobSummaryMode('');
    setJobDetailsOpen(true);
  };

  const handleSummarizeJobNotes = async () => {
    if (!selectedJob?.id) return;
    setJobSummaryLoading(true);
    setJobSummary('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}collections/${selectedJob.id}/ai/summarize`, {
        method: 'POST',
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Could not summarize notes');
      setJobSummary(json.data?.summary || '');
      setJobSummaryMode(json.data?.mode || '');
    } catch (e) {
      setJobSummary(e.message || 'Summarize failed');
      setJobSummaryMode('error');
    } finally {
      setJobSummaryLoading(false);
    }
  };

  // Edit job
  const handleEditJob = (job) => {
      setOpenDialog(false);
      const jobDriverRaw = String(job.driver || '').trim();
      const matchedDriver = jobDriverRaw
        ? drivers.find((d) => {
            const n = String(d?.name || '').trim();
            const e = String(d?.email || '').trim();
            return (n && n === jobDriverRaw) || (e && e === jobDriverRaw);
          })
        : null;
    setSelectedJob(job);
    setEditJob({
      name: job.name || '',
      client: job.client || '',
      client_id: job.client_id ?? job.client_record?.id ?? '',
        lead_source: job.lead_source || 'phone',
        is_booked: isBookedBool(job.is_booked),
      address: job.address || '',
      email: job.email || '',
      phone: job.phone || '',
      scheduled_date: job.scheduled_date
        ? combineDateAndTime(job.scheduled_date, job.scheduled_time)
        : '',
        notes: job.notes || '',
        price: job.price ?? '',
        currency: job.currency || 'GBP',
        payment_method: job.payment_method || 'cash',
        is_problem: !!job.is_problem,
        problem_reason: job.problem_reason || '',
        driver_id: matchedDriver?.id ?? '',
    });
    setEditSelectedFiles([]);
    setEditJobOpen(true);
  };

  // Save edited job
  const handleSaveEditJob = async () => {
    if (!selectedJob?.id || editSaving) return;
    try {
      setEditSaving(true);
      const token = localStorage.getItem('token');
      const clientId = resolveClientIdForJobSubmit(
        editJob.client_id,
        editJob.client,
        clientSearchOptions,
        selectedJob
      );
      const dt = splitDateTimeLocal(editJob.scheduled_date);
      const payload = {
        name: editJob.name,
        client: editJob.client,
        address: editJob.address,
        notes: editJob.notes,
        lead_source: editJob.lead_source || 'phone',
        is_booked: isBookedBool(editJob.is_booked),
        email: editJob.email || undefined,
        phone: editJob.phone || undefined,
        payment_method: editJob.payment_method || 'cash',
        is_problem: !!editJob.is_problem,
        problem_reason: editJob.is_problem ? editJob.problem_reason || '' : '',
        scheduled_date: dt.date || null,
        scheduled_time: dt.time || null,
      };
      if (editJob.price !== '' && editJob.price != null) {
        payload.price = Number(editJob.price);
        payload.currency = editJob.currency || 'GBP';
      }
      if (editJob.driver_id) {
        const selectedDriver = drivers.find((d) => String(d.id) === String(editJob.driver_id));
        payload.driver = selectedDriver ? selectedDriver.name : undefined;
      } else {
        payload.driver = null;
      }
      if (clientId != null && clientId !== '') {
        payload.client_id = clientId;
      }
      const response = await fetch(`${API_BASE_URL}collections/${selectedJob.id}`, {
        // PATCH: some hosts/proxies mishandle PUT and turn it into POST /collections (creates a duplicate job).
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const jobId = selectedJob.id;
        if (editSelectedFiles.length > 0) {
          const uploadRes = await uploadCollectionFiles(jobId, editSelectedFiles, token);
          if (!uploadRes.ok) {
            window.alert(
              `Job was saved, but some files could not be uploaded: ${uploadRes.message}`
            );
          }
        }
        setEditJobOpen(false);
        setSelectedJob(null);
        setEditSelectedFiles([]);
        setClientSearchOptions([]);
        void fetchJobs({ force: true });
      } else {
        const ct = response.headers.get('content-type') || '';
        let msg = `Update failed (${response.status})`;
        if (ct.includes('application/json')) {
          const err = await response.json().catch(() => ({}));
          msg = err.message || err.error || JSON.stringify(err);
        } else {
          const text = await response.text().catch(() => '');
          if (text) msg = text.slice(0, 300);
        }
        window.alert(msg);
      }
    } catch (error) {
      console.error('Error updating job:', error);
      window.alert('Network error while updating the job.');
    } finally {
      setEditSaving(false);
    }
  };

  // Delete job
  const handleDeleteJob = async (jobId) => {
    if (!mayDeleteJobs) {
      alert('You do not have permission to delete jobs.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}collections/${jobId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' }
      });
      if (response.ok) {
        void fetchJobs({ force: true });
      }
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  const handleJobStatusChange = async (jobId, newStatus, extra = {}) => {
    if (!canMarkJobFieldStatus) return;
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Not logged in.');
      return;
    }
    setJobStatusUpdating(true);
    try {
      const payload = { status: newStatus, ...extra };
      const response = await fetch(`${API_BASE_URL}collections/${jobId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      const updated = data.collection || { status: newStatus };
      setJobs((prev) =>
        prev.map((j) => (j.id === jobId ? { ...j, ...updated, status: updated.status || newStatus } : j))
      );
      if (selectedJob?.id === jobId) {
        setSelectedJob((prev) => (prev ? { ...prev, ...updated, status: updated.status || newStatus } : prev));
      }
    } catch (e) {
      console.error('Error updating job status:', e);
      alert(e.message || 'Could not update job status.');
    } finally {
      setJobStatusUpdating(false);
    }
  };

  const openCancelJobDialog = (job) => {
    setJobToCancel(job);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleConfirmCancelJob = async () => {
    if (!jobToCancel?.id) return;
    const reason = cancelReason.trim();
    await handleJobStatusChange(jobToCancel.id, 'cancelled', {
      ...(reason ? { cancellation_reason: reason } : {}),
    });
    setCancelDialogOpen(false);
    setJobToCancel(null);
    setCancelReason('');
  };

  const getStatusColor = (jobOrStatus) => {
    const status = typeof jobOrStatus === 'string' ? jobOrStatus : (jobOrStatus?.status || '');
    const isProblem = typeof jobOrStatus === 'object' && !!jobOrStatus?.is_problem;

    if (isProblem) {
      return { color: '#b45309', bg: '#ffedd5', icon: <WarningIcon /> };
    }

    switch (status) {
      case 'completed': return { color: '#10b981', bg: '#dcfce7', icon: <CheckCircleIcon /> };
      case 'in_progress': return { color: '#f59e0b', bg: '#fef3c7', icon: <ScheduleIcon /> };
      case 'pending': return { color: '#64748b', bg: '#f1f5f9', icon: <ScheduleIcon /> };
      case 'cancelled': return { color: '#ef4444', bg: '#fee2e2', icon: <WarningIcon /> };
      default: return { color: '#ef4444', bg: '#fee2e2', icon: <WarningIcon /> };
    }
  };

  const searchDigitsOnly = (s) => String(s || '').replace(/\D/g, '');
  const normalizeText = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  /** UK +44 vs national 0 and partial numbers — compare after normalizing leading country code. */
  const normalizeUkPhoneDigits = (digitsOnly) => {
    let x = String(digitsOnly || '').replace(/\D/g, '');
    if (x.startsWith('44') && x.length >= 12) x = `0${x.slice(2)}`;
    return x;
  };

  const phoneDigitsLooselyMatch = (queryDigits, phoneStr) => {
    const q = normalizeUkPhoneDigits(queryDigits);
    const p = normalizeUkPhoneDigits(searchDigitsOnly(phoneStr));
    if (!q || !p) return false;
    if (p.includes(q) || q.includes(p)) return true;
    const n = Math.min(q.length, p.length);
    return n >= 7 && q.slice(-n) === p.slice(-n);
  };

  /** "#141" or "141" — job id only (not a phone fragment like …141514…). UK mobiles are longer. */
  const MAX_SHORT_JOB_ID_QUERY_DIGITS = 8;

  const jobMatchesClientSearch = (job, qRaw) => {
    const trimmed = normalizeJobSearchInput(qRaw);
    if (!trimmed) return true;

    const qDigits = searchDigitsOnly(trimmed);
    const qNorm = normalizeText(trimmed);
    const tokens = qNorm.split(/\s+/).filter(Boolean);

    const rec = job.client_record || job.clientRecord;
    const creator = job.created_by || job.createdBy;
    const owner = job.office_owner || job.officeOwner;

    const safeJson = (o) => {
      if (!o || typeof o !== 'object') return '';
      try {
        return JSON.stringify(o);
      } catch {
        return '';
      }
    };

    const hay = normalizeText(
      [
        `#${job.id ?? ''}`,
        `job ${job.id ?? ''}`,
        String(job.id ?? ''),
        job.name,
        job.client,
        job.client_name,
        rec?.name,
        rec?.email,
        rec?.phone,
        job.driver,
        job.address,
        job.email,
        job.phone,
        job.notes,
        creator?.name,
        creator?.email,
        owner?.name,
        safeJson(rec),
        safeJson(creator),
        safeJson(owner),
      ]
        .filter(Boolean)
        .join(' | ')
    );

    // Long digit strings: phone or exact string match on job id (short "#141"-style queries handled in filteredJobs).
    if (qDigits.length >= 9) {
      const phoneSources = [job.phone, rec?.phone].filter(Boolean);
      const phoneMatch = phoneSources.some((p) => phoneDigitsLooselyMatch(qDigits, p));
      const idExact = String(job.id ?? '') === qDigits;
      if (phoneMatch || idExact) return true;
    }

    // Single-term contiguous match (works when the whole word appears in one field).
    if (tokens.length === 1 && qNorm.length >= 2 && hay.includes(qNorm)) return true;

    if (!tokens.length) return false;
    return tokens.every((t) => hay.includes(t));
  };

  const jobPhoneSearchHit = (job, qRaw) => {
    const trimmed = normalizeJobSearchInput(qRaw);
    const compact = trimmed.replace(/\s+/g, '');
    const qDigits = searchDigitsOnly(trimmed);
    if (!qDigits) return false;
    if (
      /^#?\d+$/.test(compact) &&
      qDigits.length >= 1 &&
      qDigits.length <= MAX_SHORT_JOB_ID_QUERY_DIGITS
    ) {
      const idNum = parseInt(qDigits, 10);
      return !Number.isNaN(idNum) && Number(job.id) === idNum;
    }
    const rec = job.client_record || job.clientRecord;
    const phoneSources = [job.phone, rec?.phone].filter(Boolean);
    return (
      phoneSources.some((p) => phoneDigitsLooselyMatch(qDigits, p)) ||
      String(job.id || '').includes(qDigits)
    );
  };

  const filteredJobs = useMemo(() => {
    let list = filterJobsByOwnerList(jobs, ownerFilter, currentUserId);
    switch (filter) {
      case 'active':
        list = list.filter(
          (job) => job.status !== 'completed' && job.status !== 'cancelled'
        );
        break;
      case 'completed':
        list = list.filter((job) => job.status === 'completed');
        break;
      case 'cancelled':
        list = list.filter((job) => job.status === 'cancelled');
        break;
      default:
        break;
    }
    if (bookedFilter === 'booked') {
      list = list.filter((job) => isBookedBool(job.is_booked));
    } else if (bookedFilter === 'unbooked') {
      list = list.filter((job) => !isBookedBool(job.is_booked));
    }
    const qRaw = normalizeJobSearchInput(clientSearch);
    if (qRaw) {
      const compact = qRaw.replace(/\s+/g, '');
      const qDigits = searchDigitsOnly(qRaw);
      const isShortPureJobIdQuery =
        /^#?\d+$/.test(compact) &&
        qDigits.length >= 1 &&
        qDigits.length <= MAX_SHORT_JOB_ID_QUERY_DIGITS;
      if (isShortPureJobIdQuery) {
        const idNum = parseInt(qDigits, 10);
        list = Number.isNaN(idNum)
          ? []
          : list.filter((job) => Number(job.id) === idNum);
      } else {
        list = list.filter((job) => jobMatchesClientSearch(job, qRaw));
      }
    }
    // Calendar day applies only when not using text search — otherwise names/job # look “broken”.
    if (scheduleDayFilter && !normalizeJobSearchInput(clientSearch)) {
      list = list.filter((job) => jobMatchesScheduleDayFilter(job, scheduleDayFilter));
    }
    if (newOnlineOnlyFilter) {
      list = list.filter((job) => isNewOnlineBooking(job));
    }
    return list;
  }, [jobs, ownerFilter, currentUserId, filter, bookedFilter, clientSearch, scheduleDayFilter, newOnlineOnlyFilter]);

  // Saved filters (date search, owner mine, etc.) often hide every job after navigation — clear once.
  useEffect(() => {
    didAutoClearFiltersRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if (loading || didAutoClearFiltersRef.current) return undefined;
    if (jobs.length > 0 && filteredJobs.length === 0) {
      didAutoClearFiltersRef.current = true;
      clearListFilters();
    }
    return undefined;
  }, [jobs.length, filteredJobs.length, loading, clearListFilters]);

  const newOnlineBookingCount = useMemo(() => countNewOnlineBookings(jobs), [jobs]);

  const ippNum = Number(itemsPerPage) || 6;
  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / ippNum));

  useEffect(() => {
    const ipp = Number(itemsPerPage) || 6;
    const tp = Math.max(1, Math.ceil(filteredJobs.length / ipp));
    setCurrentPage((p) => (p > tp ? tp : p));
  }, [filteredJobs.length, itemsPerPage]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    setFilter(event.target.value);
    setCurrentPage(1); // Reset to first page
  };

  const handleBookedFilterChange = (event) => {
    setBookedFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleClientSearchChange = (event) => {
    setClientSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleExportJobsCsv = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}export/collections`, {
        headers: {
          Accept: 'text/csv',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jobs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      window.alert('Export failed. Ensure you are logged in as admin/office role.');
    }
  };

  const handleDownloadDailyPdf = async () => {
    const token = localStorage.getItem('token');
    const date = (dailyPdfDate || new Date().toISOString().slice(0, 10)).trim();
    try {
      const qs = new URLSearchParams();
      if (date) qs.set('date', date);
      const res = await fetch(`${API_BASE_URL}collections/daily-pdf?${qs.toString()}`, {
        headers: {
          Accept: 'application/pdf',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily-jobs-${date || new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      window.alert('PDF export failed. Ensure you are logged in as admin/office role.');
    }
  };

  const normalizeClientName = (s) => (s || '').trim().toLowerCase();

  const getClientOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    if (!option) return '';
    const parts = [`#${option.id}`, option.name || ''].filter(Boolean);
    return parts.join(' · ');
  };

  const shouldClearClientIdOnText = (text, clientId, options) => {
    if (clientId === '' || clientId == null) return false;
    const opt = options.find((o) => String(o.id) === String(clientId));
    if (!opt) return false;
    return normalizeClientName(opt.name) !== normalizeClientName(text);
  };

  const resolveClientIdForJobSubmit = (
    explicitId,
    clientName,
    searchOptions,
    selectedJobRow
  ) => {
    const idNum =
      explicitId === '' || explicitId == null ? null : Number(explicitId);
    const n = normalizeClientName(clientName);

    if (idNum != null && !Number.isNaN(idNum)) {
      const opt = searchOptions.find((c) => Number(c.id) === idNum);
      if (opt && normalizeClientName(opt.name) === n) return idNum;
      const rec = selectedJobRow?.client_record;
      if (rec != null && Number(rec.id) === idNum && normalizeClientName(rec.name || '') === n) {
        return idNum;
      }
      const jobCid = selectedJobRow?.client_id;
      if (
        (Number(jobCid) === idNum || (rec != null && Number(rec.id) === idNum)) &&
        normalizeClientName(selectedJobRow?.client || '') === n
      ) {
        return idNum;
      }
    }

    if (!n) return undefined;
    const fromOptions = searchOptions.find(
      (x) => x.name && normalizeClientName(x.name) === n
    );
    if (fromOptions?.id != null) return fromOptions.id;
    if (selectedJobRow && normalizeClientName(selectedJobRow.client || '') === n) {
      return selectedJobRow.client_id ?? selectedJobRow.client_record?.id;
    }
    return undefined;
  };

  const completedJobs = jobs.filter((job) => job.status === 'completed');
  const cancelledJobs = jobs.filter((job) => job.status === 'cancelled');
  const activeJobs = jobs.filter(
    (job) => job.status !== 'completed' && job.status !== 'cancelled'
  );

  const JobCard = ({ job }) => {
    const statusStyle = getStatusColor(job);
    const booked = isBookedBool(job.is_booked);
    const statusLabel = (job.status || 'pending').replace('_', ' ');

    return (
      <Card
        sx={{
          width: '340px',
          minHeight: 340,
          maxHeight: 'min(620px, 85vh)',
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <CardContent
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            pt: '24px !important',
            '&:last-child': { pb: 2 },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: theme.palette.text.primary, 
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: 1.2,
                }}
              >
                {job.name || 'Unnamed Job'}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: 1.2,
                }}
              >
                Job #{job.id}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              {isNewOnlineBooking(job) ? (
                <Chip
                  label={NEW_ONLINE_BOOKING_LABEL}
                  size="small"
                  color="secondary"
                  sx={{ fontWeight: 800, fontSize: '0.68rem', height: 22, maxWidth: '100%' }}
                />
              ) : null}
              <Chip
                icon={statusStyle.icon}
                label={statusLabel}
                size="small"
                sx={{
                  backgroundColor: statusStyle.bg,
                  color: statusStyle.color,
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  height: '24px',
                }}
              />
              <Tooltip
                title={
                  booked
                    ? 'Booking confirmed (saved flag — not read from job title)'
                    : 'Not marked as confirmed — tick “Booked / confirmed” when editing (separate from job name)'
                }
                arrow
                placement="left"
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={booked ? 'Confirmed booking' : 'Not confirmed'}
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderColor: booked ? '#10b981' : theme.palette.divider,
                    color: booked ? '#10b981' : theme.palette.text.secondary,
                    bgcolor: booked ? 'rgba(16,185,129,0.08)' : 'transparent',
                  }}
                />
              </Tooltip>
            </Box>
          </Box>

          {/* Scroll when many files / long notes; keeps action buttons visible */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 0.25,
              WebkitOverflowScrolling: 'touch',
            }}
          >
          {/* Client Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonIcon sx={{ 
              color: theme.palette.text.secondary, 
              fontSize: 16,
              display: 'flex',
              alignItems: 'center',
            }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                display: 'flex',
                alignItems: 'center',
                lineHeight: 1.4,
              }}
            >
              {job.client || 'No client assigned'}
            </Typography>
          </Box>

          {(job.phone || job.email) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
              {job.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.4 }}>
                    {job.phone}
                  </Typography>
                </Box>
              )}
              {job.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.4 }}>
                    {job.email}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Address */}
          {job.address && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
              <LocationIcon sx={{ 
                color: theme.palette.text.secondary, 
                fontSize: 16, 
                mt: 0.2,
                display: 'flex',
                alignItems: 'center',
              }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary, 
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: 1.4,
                }}
              >
                {job.address}
              </Typography>
            </Box>
          )}

          <JobEnteredScheduledDates job={job} variant="card" sx={{ mb: 2 }} />

          {/* Driver notes from portal (any status) */}
          {job.driver_notes && (
            <Accordion sx={{ mb: 2, boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon sx={{ 
                    color: theme.palette.primary.main, 
                    fontSize: 18,
                    display: 'flex',
                    alignItems: 'center',
                  }} />
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: theme.palette.text.primary,
                      display: 'flex',
                      alignItems: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    Driver notes
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.text.secondary, 
                    lineHeight: 1.6,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {job.driver_notes}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Field uploads (driver portal); visible for any status */}
          {job.files && job.files.length > 0 && (
            <Accordion sx={{ mb: 2, boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachFileIcon sx={{ 
                    color: theme.palette.primary.main, 
                    fontSize: 18,
                    display: 'flex',
                    alignItems: 'center',
                  }} />
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: theme.palette.text.primary,
                      display: 'flex',
                      alignItems: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    Field files ({job.files.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {job.files.map((file, index) => {
                    // Handle different file formats - could be string URL or object
                    const fileName = typeof file === 'string' 
                      ? file.split('/').pop() || 'Unknown file'
                      : file.name || file.filename || 'Unknown file';
                    
                    // Construct proper URL for Laravel storage
                    let fileUrl = '#';
                    if (typeof file === 'string') {
                      fileUrl = file;
                    } else if (file.path) {
                      // For Laravel storage files, construct the proper URL
                      fileUrl = collectionFileDisplayUrl(file);
                    } else if (file.url) {
                      fileUrl = file.url;
                    }
                    const isImage =
                      typeof file?.mime_type === 'string' && file.mime_type.startsWith('image/');
                    
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          flexDirection: isImage ? 'column' : 'row',
                          alignItems: isImage ? 'stretch' : 'center',
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: 'rgba(124, 58, 237, 0.04)',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(124, 58, 237, 0.08)',
                          },
                        }}
                        onClick={() => {
                          if (fileUrl !== '#') {
                            window.open(fileUrl, '_blank');
                          } else {
                            console.error('Invalid file URL:', file);
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <AttachFileIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
                          <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                            {fileName}
                          </Typography>
                          <VisibilityIcon sx={{ color: theme.palette.primary.main, fontSize: 16, ml: 'auto' }} />
                        </Box>
                        {isImage && fileUrl !== '#' ? (
                          <Box
                            component="img"
                            src={fileUrl}
                            alt={fileName}
                            sx={{
                              width: '100%',
                              maxWidth: 220,
                              maxHeight: 140,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              alignSelf: 'flex-start',
                            }}
                            loading="lazy"
                          />
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
          </Box>

          {/* Actions — always pinned to bottom of card */}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              gap: 1,
              pt: 1.5,
              mt: 'auto',
              alignItems: 'center',
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Tooltip title="View details">
              <IconButton 
                size="small" 
                sx={{ 
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleViewJob(job)}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit job">
              <IconButton 
                size="small" 
                sx={{ 
                  color: theme.palette.warning.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleEditJob(job)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            {mayDeleteJobs && (
              <Tooltip title="Delete job">
                <IconButton
                  size="small"
                  sx={{
                    color: theme.palette.error.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => handleDeleteJob(job.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <LinearProgress sx={{ width: '100%', maxWidth: 400 }} />
        </Box>
      </Box>
    );
  }

  // Leaflet marker icon fix
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  });

  const searchTrimmed = normalizeJobSearchInput(clientSearch);
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const sortedJobs = [...filteredJobs].sort((a, b) => {
    if (searchTrimmed && searchDigitsOnly(searchTrimmed)) {
      const pa = jobPhoneSearchHit(a, searchTrimmed) ? 0 : 1;
      const pb = jobPhoneSearchHit(b, searchTrimmed) ? 0 : 1;
      if (pa !== pb) return pa - pb;
    }
    return (
      new Date(b.created_at || b.scheduled_date) -
      new Date(a.created_at || a.scheduled_date)
    );
  });
  const paginatedJobs = sortedJobs.slice(
    (safePage - 1) * ippNum,
    safePage * ippNum
  );

  return (
    <Box sx={{ width: '100%', pb: { xs: 10, sm: 12 } }}>
      {refreshing ? (
        <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
      ) : null}
      {jobsLoadError ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reloadJobsList}>
              Reload
            </Button>
          }
        >
          {jobsLoadError}
        </Alert>
      ) : null}
      {!loading && jobs.length > 0 && filteredJobs.length === 0 ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={clearListFilters}>
              Clear filters
            </Button>
          }
        >
          {`Filters are hiding ${jobs.length} loaded job${jobs.length === 1 ? '' : 's'}.`}
        </Alert>
      ) : null}
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700, 
              color: theme.palette.text.primary, 
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              lineHeight: 1.2,
            }}
          >
            Jobs Management
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: theme.palette.text.secondary,
              display: 'flex',
              alignItems: 'center',
              lineHeight: 1.4,
            }}
          >
            Manage all your waste collection jobs and track their progress
            {jobs.length > 0 ? (
              <Typography component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.85rem' }}>
                {jobs.length} loaded
                {filteredJobs.length !== jobs.length
                  ? ` · ${filteredJobs.length} shown after filters`
                  : ''}
              </Typography>
            ) : null}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setOpenDialog(true);
            // Reset newJob state when opening dialog
            setNewJob({ 
              name: '', 
              client: '', 
              client_id: '',
              address: '', 
              email: '',
              phone: '',
              scheduled_date: '', 
              notes: '',
              files: [],
              lat: null,
              lng: null,
              driver_id: ''
            });
            setSelectedFiles([]);
          }}
          sx={{
            background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
            },
            display: 'flex',
            alignItems: 'center',
            height: '40px',
          }}
        >
          Create New Job
        </Button>
      </Box>

      {newOnlineBookingCount > 0 ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            newOnlineOnlyFilter ? (
              <Button color="inherit" size="small" onClick={() => setNewOnlineOnlyFilter(false)} sx={{ fontWeight: 700 }}>
                Show all
              </Button>
            ) : (
              <Button color="inherit" size="small" onClick={() => setNewOnlineOnlyFilter(true)} sx={{ fontWeight: 700 }}>
                Filter
              </Button>
            )
          }
        >
          <strong>
            {newOnlineBookingCount} {NEW_ONLINE_BOOKING_LABEL.toLowerCase()}
            {newOnlineBookingCount !== 1 ? 's' : ''}
          </strong>{' '}
          — pending requests from the public booking page.
        </Alert>
      ) : null}

      {/* Filters and Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {newOnlineBookingCount > 0 ? (
            <Chip
              label={`${NEW_ONLINE_BOOKING_LABEL} (${newOnlineBookingCount})`}
              color={newOnlineOnlyFilter ? 'secondary' : 'default'}
              variant={newOnlineOnlyFilter ? 'filled' : 'outlined'}
              onClick={() => {
                setNewOnlineOnlyFilter((v) => !v);
                setCurrentPage(1);
              }}
              sx={{ fontWeight: 700, cursor: 'pointer' }}
            />
          ) : null}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={filter}
              label="Filter"
              onChange={handleFilterChange}
            >
              <MenuItem value="all">All Jobs ({jobs.length})</MenuItem>
              <MenuItem value="active">Active ({activeJobs.length})</MenuItem>
              <MenuItem value="completed">Completed ({completedJobs.length})</MenuItem>
              <MenuItem value="cancelled">Cancelled ({cancelledJobs.length})</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Booking</InputLabel>
            <Select value={bookedFilter} label="Booking" onChange={handleBookedFilterChange}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="booked">Confirmed only</MenuItem>
              <MenuItem value="unbooked">Not confirmed</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Per Page</InputLabel>
            <Select
              value={itemsPerPage}
              label="Per Page"
              onChange={handleItemsPerPageChange}
            >
              <MenuItem value={6}>6 items</MenuItem>
              <MenuItem value={5}>5 items</MenuItem>
              <MenuItem value={10}>10 items</MenuItem>
              <MenuItem value={20}>20 items</MenuItem>
              <MenuItem value={50}>50 items</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Owner</InputLabel>
            <Select
              value={ownerFilter}
              label="Owner"
              onChange={(e) => {
                setOwnerFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <MenuItem value="all">All owners</MenuItem>
              <MenuItem value="mine">My jobs</MenuItem>
              {userRole === 'super_admin' && (
                officeAdminsLoading ? (
                  <MenuItem value="__loading__" disabled>
                    Loading office admins...
                  </MenuItem>
                ) : officeAdmins.length > 0 ? (
                  officeAdmins.map((u) => (
                    <MenuItem key={u?.id} value={`office_${u?.id}`}>
                      {u?.name || u?.email || `Office admin #${u?.id}`}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="__none__" disabled>
                    No office admins found
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Search"
            placeholder="Name, phone, or job #…"
            value={clientSearch}
            onChange={handleClientSearchChange}
            sx={{ minWidth: { xs: '100%', sm: 220 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportJobsCsv}
            sx={{ textTransform: 'none' }}
          >
            Export CSV
          </Button>
          <TextField
            size="small"
            type="date"
            label="View jobs on"
            value={scheduleDayFilter}
            onChange={(e) => {
              setScheduleDayFilter(e.target.value);
              setCurrentPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
            helperText={
              normalizeJobSearchInput(clientSearch) && scheduleDayFilter
                ? 'Day filter paused while searching — clear search to filter by day again'
                : scheduleDayFilter
                  ? 'List & map show this day only'
                  : undefined
            }
            InputProps={{
              endAdornment: scheduleDayFilter ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear date filter"
                    onClick={() => {
                      setScheduleDayFilter('');
                      setCurrentPage(1);
                    }}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <TextField
            size="small"
            type="date"
            label="PDF date"
            value={dailyPdfDate}
            onChange={(e) => setDailyPdfDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={handleDownloadDailyPdf}
            sx={{ textTransform: 'none' }}
          >
            Daily PDF
          </Button>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              display: 'flex',
              alignItems: 'center',
              lineHeight: 1.2,
            }}
          >
            Showing{' '}
            {filteredJobs.length === 0
              ? 0
              : (safePage - 1) * ippNum + 1}{' '}
            to {Math.min(safePage * ippNum, filteredJobs.length)} of {filteredJobs.length} jobs
          </Typography>
        </Box>
      </Box>

      {/* Jobs Grid */}
      {filteredJobs.length > 0 ? (
        <>
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={3}>
              {paginatedJobs.map((job) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={job.id}>
                  <JobCard job={job} />
                </Grid>
              ))}
            </Grid>
          </Box>
          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={safePage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      ) : (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Box
            sx={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #f1f5f920 0%, #f1f5f910 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              mx: 'auto',
              mb: 2,
            }}
          >
            <AssignmentIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No jobs found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
            {normalizeJobSearchInput(clientSearch)
              ? `No jobs match "${normalizeJobSearchInput(clientSearch)}" (name, phone, or job #)`
              : scheduleDayFilter
                ? `No jobs for ${scheduleDayFilter} with the current filters. Try another date or clear “View jobs on”.`
                : filter === 'all'
                  ? 'Create a new job to get started'
                  : `No ${filter} jobs found`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => void fetchJobs()}>
              Reload jobs
            </Button>
            {(clientSearch || scheduleDayFilter || filter !== 'all' || bookedFilter !== 'all' || ownerFilter !== 'all' || newOnlineOnlyFilter) && (
              <Button variant="outlined" onClick={clearListFilters}>
                Clear filters
              </Button>
            )}
          </Box>
        </Card>
      )}

      {/* Create Job Dialog */}
              <Dialog open={openDialog} onClose={() => {
          setOpenDialog(false);
          // Reset newJob state when closing dialog
                      setNewJob({ 
              name: '', 
              client: '', 
              client_id: '',
              address: '', 
              email: '',
              phone: '',
              scheduled_date: '', 
              notes: '',
              files: [],
              lat: null,
              lng: null,
              driver_id: ''
            });
          setSelectedFiles([]);
          setClientSearchOptions([]);
        }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Create New Job
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Job Name"
              value={newJob.name}
              onChange={(e) => setNewJob({ ...newJob, name: e.target.value })}
              fullWidth
              placeholder="Enter job name"
            />
            <Autocomplete
              freeSolo
              openOnFocus
              options={clientSearchOptions}
              filterOptions={(options) => options}
              loading={clientSearchLoading}
              getOptionLabel={getClientOptionLabel}
              isOptionEqualToValue={(a, b) =>
                typeof a === 'object' &&
                typeof b === 'object' &&
                a?.id != null &&
                b?.id != null &&
                Number(a.id) === Number(b.id)
              }
              inputValue={newJob.client}
              onInputChange={(e, value, reason) => {
                if (reason === 'reset') return;
                if (reason === 'clear') {
                  setNewJob((prev) => ({ ...prev, client: '', client_id: '' }));
                  fetchClientsForJobForm(
                    '',
                    editJobOpen ? selectedJob?.client_record : null
                  );
                  return;
                }
                if (reason === 'input') {
                  setNewJob((prev) => ({
                    ...prev,
                    client: value,
                    client_id: shouldClearClientIdOnText(
                      value,
                      prev.client_id,
                      clientSearchOptions
                    )
                      ? ''
                      : prev.client_id,
                  }));
                  fetchClientsForJobForm(
                    value,
                    editJobOpen ? selectedJob?.client_record : null
                  );
                }
              }}
              onChange={(e, value) => {
                if (value == null) {
                  setNewJob((prev) => ({ ...prev, client: '', client_id: '' }));
                  return;
                }
                if (typeof value === 'object' && value.id != null) {
                  setNewJob((prev) => ({
                    ...prev,
                    client: value.name || '',
                    client_id: value.id,
                    email: value.email || prev.email,
                    phone: value.phone || prev.phone,
                    ...(value.address ? { address: value.address } : {}),
                  }));
                  return;
                }
                if (typeof value === 'string') {
                  setNewJob((prev) => ({
                    ...prev,
                    client: value,
                    client_id: shouldClearClientIdOnText(
                      value,
                      prev.client_id,
                      clientSearchOptions
                    )
                      ? ''
                      : prev.client_id,
                  }));
                }
              }}
              renderOption={(props, option) => {
                if (typeof option === 'string') {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }
                return (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">
                        #{option.id} · {option.name}
                      </Typography>
                      {(option.phone || option.email) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {[option.phone, option.email].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              noOptionsText="No matches — try name, phone, or client #"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  placeholder="Search by name, phone, # or enter a new client"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {clientSearchLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              label="Client Email"
              type="email"
              value={newJob.email}
              onChange={(e) => setNewJob({ ...newJob, email: e.target.value })}
              fullWidth
              placeholder="client@example.com"
            />
            <TextField
              label="Client Phone"
              type="tel"
              value={newJob.phone}
              onChange={(e) => setNewJob({ ...newJob, phone: e.target.value })}
              fullWidth
              placeholder="+44 7700 900123"
            />
            <TextField
              label="Address"
              value={newJob.address}
              onChange={async (e) => {
                const address = e.target.value;
                setNewJob(prev => ({ ...prev, address }));
                
                // Geocode the address when it's long enough
                if (address.length > 5) {
                  try {
                    // Add "London, UK" to improve geocoding accuracy for London addresses
                    const searchQuery = address.includes('London') ? address : `${address}, London, UK`;
                    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&countrycodes=gb&limit=1`);
                    const data = await res.json();
                    
                    if (data && data[0]) {
                      const lat = parseFloat(data[0].lat);
                      const lng = parseFloat(data[0].lon);
                      
                      setNewJob(prev => ({
                        ...prev,
                        address: data[0].display_name, // Use the full formatted address
                        lat,
                        lng
                      }));
                    } else {
                      // Clear coordinates if geocoding fails
                      setNewJob(prev => ({ ...prev, address, lat: null, lng: null }));
                    }
                  } catch (error) {
                    console.error('Geocoding error:', error);
                    // Clear coordinates on error
                    setNewJob(prev => ({ ...prev, address, lat: null, lng: null }));
                  }
                } else {
                  // Clear coordinates for short addresses
                  setNewJob(prev => ({ ...prev, address, lat: null, lng: null }));
                }
              }}
              fullWidth
              margin="normal"
              placeholder="Enter job address (will be geocoded automatically)"
            />
            {/* Scheduled Date & Time - moved above the map to avoid cramped layout */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Scheduled Date & Time"
                type="datetime-local"
                value={newJob.scheduled_date}
                onChange={(e) => setNewJob({ ...newJob, scheduled_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, minWidth: 260 }}
                fullWidth
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Lead source</InputLabel>
              <Select
                value={newJob.lead_source || 'phone'}
                label="Lead source"
                onChange={(e) => setNewJob({ ...newJob, lead_source: e.target.value })}
              >
                {LEAD_SOURCE_OPTIONS.filter((o) => o.value !== 'estimate').map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!newJob.is_booked}
                  onChange={(e) => setNewJob((prev) => ({ ...prev, is_booked: e.target.checked }))}
                />
              }
              label="Booked / confirmed"
            />
            <FormControl fullWidth>
              <InputLabel>Payment method</InputLabel>
              <Select
                value={newJob.payment_method || 'cash'}
                label="Payment method"
                onChange={(e) => setNewJob({ ...newJob, payment_method: e.target.value })}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="card">Card</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Price"
                type="number"
                value={newJob.price}
                onChange={(e) => setNewJob({ ...newJob, price: e.target.value })}
                sx={{ flex: 1, minWidth: 180 }}
                inputProps={{ min: 0, step: '0.01' }}
              />
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={newJob.currency || 'GBP'}
                  label="Currency"
                  onChange={(e) => setNewJob({ ...newJob, currency: e.target.value })}
                >
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="BGN">BGN</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!newJob.is_problem}
                  onChange={(e) =>
                    setNewJob((prev) => ({
                      ...prev,
                      is_problem: e.target.checked,
                      ...(e.target.checked ? {} : { problem_reason: '' }),
                    }))
                  }
                />
              }
              label="Mark as problem job"
            />
            {newJob.is_problem && (
              <TextField
                label="Problem reason"
                value={newJob.problem_reason || ''}
                onChange={(e) => setNewJob({ ...newJob, problem_reason: e.target.value })}
                fullWidth
                multiline
                rows={2}
                placeholder="Describe the issue"
              />
            )}
            {/* Map for new job - moved to bottom for better spacing */}
            {/* Driver Selection */}
            <FormControl fullWidth>
              <InputLabel>Assign Driver (Optional)</InputLabel>
              <Select
                value={newJob.driver_id}
                label="Assign Driver (Optional)"
                onChange={(e) => setNewJob({ ...newJob, driver_id: e.target.value })}
              >
                <MenuItem value="">
                  <em>No driver assigned</em>
                </MenuItem>
                {drivers.map((driver) => (
                  <MenuItem key={driver.id} value={driver.id}>
                    {driver.name} {driver.surname ? `${driver.surname}` : ''} 
                    {driver.email && ` (${driver.email})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Notes"
              value={newJob.notes}
              onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Add any notes for the job"
            />

            {/* Map for new job - placed after notes for clearer layout */}
            <Box sx={{ width: '100%', height: '300px', mt: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                Job Location Preview
              </Typography>
              <MapContainer 
                center={newJob.lat && newJob.lng ? [newJob.lat, newJob.lng] : [51.5074, -0.1278]} 
                zoom={newJob.lat && newJob.lng ? 15 : 10} 
                style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                ref={mapRef}
              >
                <MapUpdater lat={newJob.lat} lng={newJob.lng} />
                <TileLayer
                  attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {newJob.lat && newJob.lng ? (
                  <Marker 
                    position={[newJob.lat, newJob.lng]}
                    icon={L.divIcon({
                      className: 'custom-marker',
                      html: `<div style="
                        background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
                        color: white;
                        width: 40px;
                        height: 40px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: bold;
                        font-size: 16px;
                        border: 3px solid white;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                      ">📍</div>`,
                      iconSize: [40, 40],
                      iconAnchor: [20, 40]
                    })}
                  >
                    <Popup>
                      <Box sx={{ p: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {newJob.name || 'New Job'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {newJob.address}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
                          Coordinates: {newJob.lat.toFixed(6)}, {newJob.lng.toFixed(6)}
                        </Typography>
                      </Box>
                    </Popup>
                  </Marker>
                ) : (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1000,
                    textAlign: 'center',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    padding: 2,
                    borderRadius: 2,
                    border: '1px solid #e2e8f0'
                  }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Enter an address above to see the location on the map
                    </Typography>
                  </Box>
                )}
              </MapContainer>
            </Box>
            
            {/* File Upload */}
            <Box>
              <input
                type="file"
                multiple
                id="file-upload"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setSelectedFiles(files);
                }}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                component="label"
                htmlFor="file-upload"
                startIcon={<AttachFileIcon />}
                sx={{
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                Attach Files
              </Button>
              
              {/* Selected Files Display */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                    Selected files ({selectedFiles.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedFiles.map((file, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: theme.palette.action.hover,
                        }}
                      >
                        <AttachFileIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, flex: 1 }}>
                          {file.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
            {createError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {createError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: theme.palette.text.secondary }}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateJob} 
            variant="contained"
            disabled={createLoading}
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
              },
            }}
          >
            {createLoading ? 'Creating…' : 'Create Job'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job Details Dialog */}
      <Dialog open={jobDetailsOpen} onClose={() => setJobDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Job Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedJob && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                  Job Name
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  {selectedJob.name || 'Unnamed Job'}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                  Client
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  {selectedJob.client || 'No client assigned'}
                </Typography>
              </Box>

              {(selectedJob.email || selectedJob.phone) && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                    Contact
                  </Typography>
                  {selectedJob.email && (
                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                      {selectedJob.email}
                    </Typography>
                  )}
                  {selectedJob.phone && (
                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                      {selectedJob.phone}
                    </Typography>
                  )}
                </Box>
              )}
              
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                  Address
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  {selectedJob.address || 'No address'}
                </Typography>
              </Box>
              
              <JobEnteredScheduledDates job={selectedJob} variant="detail" />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                  Lead source
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  {labelForLeadSource(selectedJob.lead_source) || '—'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                  Booking confirmed
                </Typography>
                <Chip
                  label={isBookedBool(selectedJob.is_booked) ? 'Confirmed' : 'Not confirmed'}
                  size="small"
                  sx={{
                    backgroundColor: isBookedBool(selectedJob.is_booked) ? '#dcfce7' : '#f1f5f9',
                    color: isBookedBool(selectedJob.is_booked) ? '#10b981' : '#64748b',
                    fontWeight: 700,
                  }}
                />
                {selectedJob.booked_at && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: theme.palette.text.secondary }}>
                    {new Date(selectedJob.booked_at).toLocaleString()}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                  Payment method
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary, textTransform: 'capitalize' }}>
                  {selectedJob.payment_method || '—'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                  Price
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  {selectedJob.price != null && selectedJob.price !== ''
                    ? `${selectedJob.price} ${selectedJob.currency || ''}`.trim()
                    : '—'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                  Office owner
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  {selectedJob.office_owner?.name || selectedJob.office_owner?.email || '—'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                  Created by
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  {selectedJob.created_by?.name || selectedJob.created_by?.email || '—'}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                  Status
                </Typography>
                <Chip
                  label={selectedJob.status?.replace('_', ' ') || 'Unknown'}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(selectedJob).bg,
                    color: getStatusColor(selectedJob).color,
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}
                />
                {canMarkJobFieldStatus && selectedJob.status !== 'completed' && selectedJob.status !== 'cancelled' ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                    {selectedJob.status === 'pending' ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        startIcon={<PlayArrowIcon />}
                        disabled={jobStatusUpdating}
                        onClick={() => handleJobStatusChange(selectedJob.id, 'in_progress')}
                        sx={{ textTransform: 'none' }}
                      >
                        Arrived
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      disabled={jobStatusUpdating}
                      onClick={() => {
                        if (window.confirm('Mark this job as completed?')) {
                          handleJobStatusChange(selectedJob.id, 'completed');
                        }
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      {jobStatusUpdating ? 'Updating…' : 'Complete'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      disabled={jobStatusUpdating}
                      onClick={() => openCancelJobDialog(selectedJob)}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel job
                    </Button>
                  </Box>
                ) : null}
                {canMarkJobFieldStatus && selectedJob.status === 'cancelled' ? (
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={jobStatusUpdating}
                      onClick={() => {
                        if (window.confirm('Restore this job to pending?')) {
                          handleJobStatusChange(selectedJob.id, 'pending');
                        }
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Restore job
                    </Button>
                  </Box>
                ) : null}
              </Box>

              {selectedJob.status === 'cancelled' && (
                <Box sx={{ mt: 1 }}>
                  {selectedJob.cancellation_reason ? (
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      <strong>Reason:</strong> {selectedJob.cancellation_reason}
                    </Typography>
                  ) : null}
                  {selectedJob.cancelled_at ? (
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mt: 0.5 }}>
                      Cancelled: {dayjs(selectedJob.cancelled_at).format('D MMM YYYY, HH:mm')}
                    </Typography>
                  ) : null}
                </Box>
              )}

              {!!selectedJob.is_problem && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                    Problem
                  </Typography>
                  <Chip
                    label="Problem job"
                    size="small"
                    sx={{
                      backgroundColor: '#ffedd5',
                      color: '#b45309',
                      fontWeight: 700,
                    }}
                  />
                  {selectedJob.problem_reason && (
                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 1 }}>
                      {selectedJob.problem_reason}
                    </Typography>
                  )}
                </Box>
              )}
              
              {selectedJob.notes && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                    Notes
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                    {selectedJob.notes}
                  </Typography>
                </Box>
              )}
              
              {selectedJob.driver_notes && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                    Driver Notes
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                    {selectedJob.driver_notes}
                  </Typography>
                </Box>
              )}

              {canUseAi && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                      AI summary
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleSummarizeJobNotes}
                      disabled={jobSummaryLoading}
                      sx={{ textTransform: 'none' }}
                    >
                      {jobSummaryLoading ? 'Summarizing…' : 'Summarize notes'}
                    </Button>
                  </Box>
                  {jobSummary ? (
                    <Alert severity={jobSummaryMode === 'openai' ? 'success' : 'info'} sx={{ whiteSpace: 'pre-wrap' }}>
                      {jobSummary}
                      {jobSummaryMode ? (
                        <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.8 }}>
                          {jobSummaryMode === 'openai' ? 'Enhanced with OpenAI' : 'Generated locally (enable AI_ENABLED + OPENAI_API_KEY for OpenAI)'}
                        </Typography>
                      ) : null}
                    </Alert>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Combines office and driver notes into a short summary for handover or reporting.
                    </Typography>
                  )}
                </Box>
              )}
              
              {selectedJob.files && selectedJob.files.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                    Field files ({selectedJob.files.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedJob.files.map((file, index) => {
                      const fileName = typeof file === 'string' 
                        ? file.split('/').pop() || 'Unknown file'
                        : file.name || file.filename || 'Unknown file';
                      
                      // Construct proper URL for Laravel storage
                      let fileUrl = '#';
                      if (typeof file === 'string') {
                        fileUrl = file;
                      } else if (file.path) {
                        // For Laravel storage files, construct the proper URL
                        fileUrl = collectionFileDisplayUrl(file);
                      } else if (file.url) {
                        fileUrl = file.url;
                      }
                      const isImage =
                        typeof file?.mime_type === 'string' && file.mime_type.startsWith('image/');
                      
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            flexDirection: isImage ? 'column' : 'row',
                            alignItems: isImage ? 'stretch' : 'center',
                            gap: 1,
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: 'rgba(124, 58, 237, 0.04)',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(124, 58, 237, 0.08)',
                            },
                          }}
                          onClick={() => {
                            if (fileUrl !== '#') {
                              window.open(fileUrl, '_blank');
                            } else {
                              console.error('Invalid file URL:', file);
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <AttachFileIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
                            <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                              {fileName}
                            </Typography>
                            <VisibilityIcon sx={{ color: theme.palette.primary.main, fontSize: 16, ml: 'auto' }} />
                          </Box>
                          {isImage && fileUrl !== '#' ? (
                            <Box
                              component="img"
                              src={fileUrl}
                              alt={fileName}
                              sx={{
                                width: '100%',
                                maxWidth: 320,
                                maxHeight: 200,
                                objectFit: 'contain',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                alignSelf: 'flex-start',
                              }}
                              loading="lazy"
                            />
                          ) : null}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              <JobCostingPanel collectionId={selectedJob?.id} open={jobDetailsOpen} />
              <JobInventoryPanel collectionId={selectedJob?.id} open={jobDetailsOpen} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setSendMessageOpen(true)}
            disabled={!selectedJob?.phone && !selectedJob?.email}
          >
            Send message
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setJobDetailsOpen(false)} sx={{ color: theme.palette.text.secondary }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDialogOpen}
        onClose={() => {
          if (!jobStatusUpdating) {
            setCancelDialogOpen(false);
            setJobToCancel(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Cancel job</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
            {jobToCancel
              ? `Mark "${jobToCancel.client || jobToCancel.name || 'this job'}" as cancelled when the customer has pulled out.`
              : 'Mark this job as cancelled.'}
          </Typography>
          <TextField
            label="Reason (optional)"
            placeholder="e.g. Customer changed plans, found another company…"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            disabled={jobStatusUpdating}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setCancelDialogOpen(false);
              setJobToCancel(null);
            }}
            disabled={jobStatusUpdating}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancelJob}
            disabled={jobStatusUpdating}
            startIcon={<CancelIcon />}
          >
            {jobStatusUpdating ? 'Cancelling…' : 'Cancel job'}
          </Button>
        </DialogActions>
      </Dialog>

      <SendMessageDialog
        open={sendMessageOpen}
        onClose={() => setSendMessageOpen(false)}
        defaultChannel={selectedJob?.phone ? 'sms' : 'email'}
        defaultTo={selectedJob?.phone || selectedJob?.email || ''}
        subjectType="collection"
        subjectId={selectedJob?.id}
        suggestJobId={selectedJob?.id}
      />

      {/* Edit Job Dialog */}
      <Dialog
        open={editJobOpen}
        onClose={() => {
          setEditJobOpen(false);
          setEditSelectedFiles([]);
          setClientSearchOptions([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Edit Job
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Job Name"
              value={editJob.name}
              onChange={(e) => setEditJob({ ...editJob, name: e.target.value })}
              fullWidth
              placeholder="Enter job name"
            />
            <Autocomplete
              freeSolo
              openOnFocus
              options={clientSearchOptions}
              filterOptions={(options) => options}
              loading={clientSearchLoading}
              getOptionLabel={getClientOptionLabel}
              isOptionEqualToValue={(a, b) =>
                typeof a === 'object' &&
                typeof b === 'object' &&
                a?.id != null &&
                b?.id != null &&
                Number(a.id) === Number(b.id)
              }
              inputValue={editJob.client}
              onInputChange={(e, value, reason) => {
                if (reason === 'reset') return;
                if (reason === 'clear') {
                  setEditJob((prev) => ({ ...prev, client: '', client_id: '' }));
                  fetchClientsForJobForm(
                    '',
                    editJobOpen ? selectedJob?.client_record : null
                  );
                  return;
                }
                if (reason === 'input') {
                  setEditJob((prev) => ({
                    ...prev,
                    client: value,
                    client_id: shouldClearClientIdOnText(
                      value,
                      prev.client_id,
                      clientSearchOptions
                    )
                      ? ''
                      : prev.client_id,
                  }));
                  fetchClientsForJobForm(
                    value,
                    editJobOpen ? selectedJob?.client_record : null
                  );
                }
              }}
              onChange={(e, value) => {
                if (value == null) {
                  setEditJob((prev) => ({ ...prev, client: '', client_id: '' }));
                  return;
                }
                if (typeof value === 'object' && value.id != null) {
                  setEditJob((prev) => ({
                    ...prev,
                    client: value.name || '',
                    client_id: value.id,
                    email: value.email || prev.email,
                    phone: value.phone || prev.phone,
                    ...(value.address ? { address: value.address } : {}),
                  }));
                  return;
                }
                if (typeof value === 'string') {
                  setEditJob((prev) => ({
                    ...prev,
                    client: value,
                    client_id: shouldClearClientIdOnText(
                      value,
                      prev.client_id,
                      clientSearchOptions
                    )
                      ? ''
                      : prev.client_id,
                  }));
                }
              }}
              renderOption={(props, option) => {
                if (typeof option === 'string') {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }
                return (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">
                        #{option.id} · {option.name}
                      </Typography>
                      {(option.phone || option.email) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {[option.phone, option.email].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              noOptionsText="No matches — try name, phone, or client #"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  placeholder="Search by name, phone, # or enter a new client"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {clientSearchLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              label="Client Email"
              type="email"
              value={editJob.email}
              onChange={(e) => setEditJob({ ...editJob, email: e.target.value })}
              fullWidth
              placeholder="client@example.com"
            />
            <TextField
              label="Client Phone"
              type="tel"
              value={editJob.phone}
              onChange={(e) => setEditJob({ ...editJob, phone: e.target.value })}
              fullWidth
              placeholder="+44 7700 900123"
            />
            <TextField
              label="Address"
              value={editJob.address}
              onChange={(e) => setEditJob({ ...editJob, address: e.target.value })}
              fullWidth
              placeholder="Enter collection address"
              multiline
              rows={2}
            />
            <TextField
              label="Scheduled Date"
              type="datetime-local"
              value={editJob.scheduled_date}
              onChange={(e) => setEditJob({ ...editJob, scheduled_date: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel>Lead source</InputLabel>
              <Select
                value={editJob.lead_source || 'phone'}
                label="Lead source"
                onChange={(e) => setEditJob({ ...editJob, lead_source: e.target.value })}
              >
                {LEAD_SOURCE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!editJob.is_booked}
                  onChange={(e) => setEditJob((prev) => ({ ...prev, is_booked: e.target.checked }))}
                />
              }
              label="Booked / confirmed"
            />
            <FormControl fullWidth>
              <InputLabel>Payment method</InputLabel>
              <Select
                value={editJob.payment_method || 'cash'}
                label="Payment method"
                onChange={(e) => setEditJob({ ...editJob, payment_method: e.target.value })}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="card">Card</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Assign Driver (Optional)</InputLabel>
              <Select
                value={editJob.driver_id ?? ''}
                label="Assign Driver (Optional)"
                onChange={(e) => setEditJob({ ...editJob, driver_id: e.target.value })}
              >
                <MenuItem value="">
                  <em>No driver assigned</em>
                </MenuItem>
                {drivers.map((driver) => (
                  <MenuItem key={driver.id} value={driver.id}>
                    {driver.name} {driver.surname ? `${driver.surname}` : ''}
                    {driver.email && ` (${driver.email})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Price"
                type="number"
                value={editJob.price}
                onChange={(e) => setEditJob({ ...editJob, price: e.target.value })}
                sx={{ flex: 1, minWidth: 180 }}
                inputProps={{ min: 0, step: '0.01' }}
              />
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={editJob.currency || 'GBP'}
                  label="Currency"
                  onChange={(e) => setEditJob({ ...editJob, currency: e.target.value })}
                >
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="BGN">BGN</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!editJob.is_problem}
                  onChange={(e) =>
                    setEditJob((prev) => ({
                      ...prev,
                      is_problem: e.target.checked,
                      ...(e.target.checked ? {} : { problem_reason: '' }),
                    }))
                  }
                />
              }
              label="Mark as problem job"
            />
            {editJob.is_problem && (
              <TextField
                label="Problem reason"
                value={editJob.problem_reason || ''}
                onChange={(e) => setEditJob({ ...editJob, problem_reason: e.target.value })}
                fullWidth
                multiline
                rows={2}
                placeholder="Describe the issue"
              />
            )}
            <TextField
              label="Notes"
              value={editJob.notes}
              onChange={(e) => setEditJob({ ...editJob, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Add any notes for the job"
            />

            {/* Existing + new file uploads */}
            <Box>
              {selectedJob?.files && selectedJob.files.length > 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Existing files ({selectedJob.files.length}) — new uploads are added below.
                </Typography>
              ) : null}
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.heic,.heif"
                id="edit-job-file-upload"
                onChange={(e) => {
                  const picked = Array.from(e.target.files || []);
                  if (picked.length) {
                    setEditSelectedFiles((prev) => [...prev, ...picked]);
                  }
                  e.target.value = '';
                }}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                component="label"
                htmlFor="edit-job-file-upload"
                startIcon={<AttachFileIcon />}
                sx={{
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                Add photos / files
              </Button>
              {editSelectedFiles.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                    New files to upload ({editSelectedFiles.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {editSelectedFiles.map((file, index) => (
                      <Box
                        key={`${file.name}-${index}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: theme.palette.action.hover,
                        }}
                      >
                        <AttachFileIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, flex: 1 }}>
                          {file.name}
                        </Typography>
                        <Button
                          size="small"
                          color="inherit"
                          onClick={() => {
                            setEditSelectedFiles((prev) => prev.filter((_, i) => i !== index));
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => {
              setEditJobOpen(false);
              setEditSelectedFiles([]);
              setClientSearchOptions([]);
            }}
            sx={{ color: theme.palette.text.secondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEditJob}
            variant="contained"
            disabled={editSaving}
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
              },
            }}
          >
            {editSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Map - Shows all active jobs */}
      <Box
        sx={{
          width: '100%',
          mt: 2,
          mb: 6,
          pb: 5,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Active Jobs Map ({jobLocations.length} jobs)
          </Typography>
          {jobLocations.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={fitMapToJobs}
              sx={{ 
                color: theme.palette.primary.main, 
                borderColor: theme.palette.primary.main,
                fontWeight: 500,
                textTransform: 'none',
              }}
            >
              Fit All Jobs
            </Button>
          )}
        </Box>
        <Box
          sx={{
            width: '100%',
            height: '380px',
            maxHeight: '380px',
            position: 'relative',
            isolation: 'isolate',
            borderRadius: 1,
            overflow: 'hidden',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(15,23,42,0.1)',
            boxSizing: 'border-box',
            flexShrink: 0,
            '& .leaflet-container': {
              height: '100% !important',
              maxHeight: '380px',
            },
          }}
        >
          {jobLocations.length > 0 ? (
        <MapContainer 
          center={[jobLocations[0].lat, jobLocations[0].lng]}
          zoom={12} 
          style={{ height: '100%', width: '100%' }} 
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {jobLocations.map(job => {
            const statusColor = getStatusColor(job);
            return (
              <Marker 
                key={job.id} 
                position={[job.lat, job.lng]}
                icon={L.divIcon({
                  className: 'custom-marker',
                  html: `
                    <div style="
                      background: ${statusColor.bg};
                      color: ${statusColor.color};
                      border: 2px solid ${statusColor.color};
                      border-radius: 50%;
                      width: 24px;
                      height: 24px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      font-weight: bold;
                      font-size: 12px;
                      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                    ">
                      ${job.name ? job.name.charAt(0).toUpperCase() : 'J'}
                    </div>
                  `,
                  iconSize: [24, 24],
                  iconAnchor: [12, 12]
                })}
              >
                <Popup>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {job.name || 'Unnamed Job'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <b>Address:</b> {translatedAddresses[job.id] || job.address}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <b>Date:</b> {(() => {
                        const d = parseJobScheduledStart(job);
                        return d && d.isValid() ? d.format('DD/MM/YYYY') : '-';
                      })()}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <b>Time:</b> {(() => {
                        const d = parseJobScheduledStart(job);
                        return d && d.isValid() ? d.format('HH:mm') : '-';
                      })()}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <b>Client:</b> {job.client_name || job.client || '-'}
                    </Typography>
                    {job.phone && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <b>Phone:</b> {job.phone}
                      </Typography>
                    )}
                    {job.email && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <b>Email:</b> {job.email}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <b>Status:</b> 
                      <Chip 
                        label={job.status || 'pending'} 
                        size="small" 
                        sx={{ 
                          ml: 1, 
                          backgroundColor: statusColor.bg, 
                          color: statusColor.color,
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }} 
                      />
                    </Typography>
                    {job.notes && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <b>Notes:</b> {job.notes}
                      </Typography>
                    )}
                    {job.driver_notes && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <b>Driver notes:</b> {job.driver_notes}
                      </Typography>
                    )}
                    {job.files && job.files.length > 0 && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <b>Field files:</b> {job.files.length} (open job for previews)
                      </Typography>
                    )}
                  </Box>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
          ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%', 
            minHeight: '380px',
            backgroundColor: '#f8f9fa',
            borderRadius: 1,
            border: '1px dashed #dee2e6'
          }}>
            <AssignmentIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
              No Active Jobs on Map
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
              All jobs are completed or no jobs have addresses
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              Check console for debugging information
            </Typography>
          </Box>
          )}
        </Box>
      </Box>
      <Box
        aria-hidden
        sx={{
          width: '100%',
          height: { xs: '100px', sm: '120px' },
          flexShrink: 0,
          flexGrow: 0,
        }}
      />
    </Box>
  );
} 